import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getGroup, getGroupMembers, getGroupsByParent, deleteGroup, updateGroup, addGroupMember, removeGroupMember } from '../lib/firestore/groups';
import { getEventsByHost } from '../lib/firestore/events';
import { canCreateSubgroup, canInviteToGroup } from '../lib/permissions';
import { storage, db } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, writeBatch, deleteDoc } from 'firebase/firestore';
import { resolveInviteToken } from '../lib/invites';
import InviteButton from '../components/InviteButton';
import SponsorModal from '../components/SponsorModal';
import InviteFriendModal from '../components/InviteFriendModal';
import QRCodeModal from '../components/QRCodeModal';
import UserProfileModal from '../components/UserProfileModal';
import BroadcastModal from '../components/BroadcastModal';
import { isGroupChatAvailable } from '../lib/chat-utils';
import { isAdmin } from '../lib/firestore/admins';
import '../styles/GroupView.css';
import '../styles/Home.css'; // Import Home.css for event card styles

const GroupView = () => {
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const notificationId = searchParams.get('notificationId');
  const { user } = useAuthState();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Listen for delete modal event from TopNavBar
  useEffect(() => {
    const handleDeleteModal = (event) => {
      if (event.detail?.groupId === groupId) {
        setShowDeleteConfirm(true);
      }
    };
    window.addEventListener('openDeleteGroupModal', handleDeleteModal);
    return () => {
      window.removeEventListener('openDeleteGroupModal', handleDeleteModal);
    };
  }, [groupId]);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joining, setJoining] = useState(false);

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersWithData, setMembersWithData] = useState([]); // Members with user profile data
  const [allEvents, setAllEvents] = useState([]); // All events (group + subgroups)
  const [subgroups, setSubgroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [canInvite, setCanInvite] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [showInviteFriendModal, setShowInviteFriendModal] = useState(false);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [showDescriptionMore, setShowDescriptionMore] = useState(false);
  const descriptionRef = useRef(null);
  const descriptionContainerRef = useRef(null);
  
  // Check if description needs "more" button on small screens
  useEffect(() => {
    const checkDescriptionHeight = () => {
      if (descriptionRef.current && window.innerWidth <= 767) {
        const lineHeight = parseFloat(getComputedStyle(descriptionRef.current).lineHeight);
        const maxHeight = lineHeight * 2; // 2 lines
        const actualHeight = descriptionRef.current.scrollHeight;
        setShowDescriptionMore(actualHeight > maxHeight);
      } else {
        setShowDescriptionMore(false);
      }
    };
    
    if (group?.description) {
      // Check after a short delay to ensure DOM is rendered
      const timer = setTimeout(checkDescriptionHeight, 100);
      window.addEventListener('resize', checkDescriptionHeight);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', checkDescriptionHeight);
      };
    }
  }, [group?.description]);
  const [showSponsoredMembers, setShowSponsoredMembers] = useState(false);
  const [sponsoredMembers, setSponsoredMembers] = useState([]);
  const [sponsoredMembersWithData, setSponsoredMembersWithData] = useState([]);
  const createDropdownRef = useRef(null);

  useEffect(() => {
    console.log('🔵 [GroupView] useEffect triggered:', { groupId, hasUser: !!user, userObject: user });
    if (!groupId) {
      console.log('🔴 [GroupView] Missing groupId');
      navigate('/home');
      return;
    }
    
    if (user) {
      localStorage.setItem(`group_visited_${groupId}`, new Date().toISOString());
      console.log('🔵 [GroupView] Starting loadGroupData...');
      loadGroupData();
    } else {
      console.log('🔵 [GroupView] No user yet, waiting for auth...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, user]);

  // Close actions menu and create dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on a dropdown item - if so, don't close (let the item's handler do it)
      const isDropdownItem = event.target.closest('.create-dropdown-item');
      if (isDropdownItem) {
        return; // Let the dropdown item's onClick handle it
      }

      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setShowCreateDropdown(false);
      }
    };

    if (showCreateDropdown) {
      // Use 'click' instead of 'mousedown' to allow dropdown item clicks to fire first
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showCreateDropdown]);

  const loadGroupData = async () => {
    if (!groupId || !user) {
      console.error('🔴 [GroupView] Missing groupId or user:', { groupId, user: !!user });
      if (!user) {
        navigate('/login');
      } else {
        navigate('/home');
      }
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify auth state before making Firestore calls
      const { auth, waitForAuth } = await import('../firebase/config');
      
      // Wait for auth to be ready
      const currentUser = await waitForAuth();
      console.log('🔵 [GroupView] Auth check:', {
        hasUser: !!user,
        hasCurrentUser: !!currentUser,
        userId: user?.uid,
        currentUserId: currentUser?.uid,
        authReady: auth.currentUser !== null
      });
      
      if (!currentUser) {
        console.error('🔴 [GroupView] No current user in auth, redirecting to login');
        navigate('/login');
        return;
      }

      console.log('🔵 [GroupView] Loading group:', groupId);
      const groupData = await getGroup(groupId);
      console.log('🔵 [GroupView] Group data loaded:', groupData);
      if (!groupData) {
        console.error('🔴 [GroupView] Group not found:', groupId);
        setError('Group not found');
        setLoading(false);
        return;
      }
      setGroup(groupData);

      try {
        console.log('🔵 [GroupView] Calling getGroupMembers...');
        console.log('🔵 [GroupView] Auth state before getGroupMembers:', {
          currentUser: auth.currentUser?.uid,
          user: user?.uid
        });
        const membersList = await getGroupMembers(groupId);
        console.log('🔵 [GroupView] Members loaded:', membersList.length);
        setMembers(membersList);
        
        // Load user data for each member
        const membersWithUserData = await Promise.all(
          membersList.map(async (member) => {
            try {
              const userDoc = await getDoc(doc(db, 'users', member.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                let profilePicture = userData.profilePicture || null;
                
                // Refresh profile picture URL to get fresh token (fixes Chrome loading issues)
                if (profilePicture) {
                  try {
                    const profileRef = ref(storage, `profile-pictures/${member.userId}`);
                    profilePicture = await getDownloadURL(profileRef);
                    console.log(`✅ [GroupView] Refreshed profile picture URL for ${member.userId}`);
                  } catch (error) {
                    console.log(`⚠️ [GroupView] Could not refresh profile picture URL for ${member.userId}, using stored URL:`, error.message);
                    // Just use stored URL as-is, no timestamp
                  }
                }
                
                return {
                  ...member,
                  firstName: userData.firstName || '',
                  lastName: userData.lastName || '',
                  profilePicture: profilePicture,
                };
              }
              return member;
            } catch (error) {
              console.error(`Error loading user data for ${member.userId}:`, error);
              return member;
            }
          })
        );
        setMembersWithData(membersWithUserData);

        const userMember = membersList.find((m) => m.userId === user.uid);
        console.log('🔵 [GroupView] User member found:', userMember);
        setUserRole(userMember?.role || null);
        const admin = await isAdmin(user.uid);
        setUserIsAdmin(admin);

        // Load sponsored members (members sponsored by current user) and pending invites
        if (userMember) {
          const sponsored = membersList.filter(m => m.sponsoredBy === user.uid);
          setSponsoredMembers(sponsored);
          
          // Load pending sponsor invites (notifications sent by current user)
          try {
            // Query invites collection for tokens issued by current user for this group
            const invitesRef = collection(db, 'invites');
            const inviteQuery = query(
              invitesRef,
              where('issuedBy', '==', user.uid),
              where('target.kind', '==', 'group_join'),
              where('target.id', '==', groupId)
            );
            const inviteSnapshot = await getDocs(inviteQuery);
            
            const pendingInvites = [];
            for (const inviteDoc of inviteSnapshot.docs) {
              const inviteData = inviteDoc.data();
              // Check if this invite has been resolved (user joined)
              const targetUserId = inviteData.metadata?.targetUserId;
              const isAccepted = targetUserId && membersList.find(m => m.userId === targetUserId);
              
              if (!isAccepted) {
                // This is a pending invite
                pendingInvites.push({
                  userId: targetUserId || null,
                  inviteId: inviteDoc.id,
                  inviteToken: inviteDoc.id,
                  status: 'pending',
                  createdAt: inviteData.createdAt?.toDate?.() || new Date(),
                });
              }
            }
            
            // Combine accepted members and pending invites
            const allSponsored = [
              ...sponsored.map(m => ({ ...m, status: 'accepted' })),
              ...pendingInvites
            ];
            
          // Load user data for sponsored members and pending invites
          const sponsoredWithData = await Promise.all(
            allSponsored.map(async (item) => {
              if (!item.userId) {
                // Pending invite without user ID - show placeholder
                return {
                  ...item,
                  firstName: 'Pending',
                  lastName: 'Invite',
                  profilePicture: null,
                };
              }
              try {
                const userDoc = await getDoc(doc(db, 'users', item.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    ...item,
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    profilePicture: userData.profilePicture || null,
                  };
                }
                return {
                  ...item,
                  firstName: 'Unknown',
                  lastName: 'User',
                  profilePicture: null,
                };
              } catch (error) {
                console.error(`Error loading user data for sponsored member ${item.userId}:`, error);
                return {
                  ...item,
                  firstName: 'Unknown',
                  lastName: 'User',
                  profilePicture: null,
                };
              }
            })
          );
          setSponsoredMembersWithData(sponsoredWithData);
          } catch (error) {
            console.error('Error loading pending invites:', error);
            // Fallback to just accepted members
            const sponsoredWithData = await Promise.all(
              sponsored.map(async (member) => {
                try {
                  const userDoc = await getDoc(doc(db, 'users', member.userId));
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    return {
                      ...member,
                      status: 'accepted',
                      firstName: userData.firstName || '',
                      lastName: userData.lastName || '',
                      profilePicture: userData.profilePicture || null,
                    };
                  }
                  return { ...member, status: 'accepted' };
                } catch (error) {
                  return { ...member, status: 'accepted' };
                }
              })
            );
            setSponsoredMembersWithData(sponsoredWithData);
          }
        }

        // If user is not a member and there's a notificationId, show join modal
        if (!userMember && notificationId) {
          setShowJoinModal(true);
        }

        console.log('🔵 [GroupView] Checking permissions...');
        try {
          const canCreateSub = await canCreateSubgroup(groupId, user.uid);
          console.log('🔵 [GroupView] Can create subgroup:', canCreateSub);
          setCanCreate(canCreateSub);
        } catch (permError) {
          console.error('🔴 [GroupView] Error checking canCreateSubgroup:', permError);
          // Don't fail the whole page if permission check fails
        }
        
        try {
          const canInv = await canInviteToGroup(groupId, user.uid);
          console.log('🔵 [GroupView] Can invite:', canInv);
          setCanInvite(canInv);
        } catch (permError) {
          console.error('🔴 [GroupView] Error checking canInviteToGroup:', permError);
          // Don't fail the whole page if permission check fails
        }

        // Load subgroups
        console.log('🔵 [GroupView] Loading subgroups...');
        let subgroupsList = [];
        try {
          subgroupsList = await getGroupsByParent('group', groupId);
          console.log('🔵 [GroupView] Subgroups loaded:', subgroupsList.length);
          setSubgroups(subgroupsList);
        } catch (subgroupError) {
          console.error('🔴 [GroupView] Error loading subgroups:', subgroupError);
          console.error('🔴 [GroupView] Error code:', subgroupError.code);
          console.error('🔴 [GroupView] Error message:', subgroupError.message);
          // If it's a missing index error, log it but don't fail the whole page
          if (subgroupError.code === 'failed-precondition') {
            console.warn('⚠️ [GroupView] Subgroups query needs Firestore index. Deploy indexes with: firebase deploy --only firestore:indexes');
            setSubgroups([]); // Set empty array so page still loads
          } else if (subgroupError.code === 'permission-denied') {
            console.error('🔴 [GroupView] Permission denied loading subgroups. Check Firestore rules and ensure user is authenticated.');
            setSubgroups([]); // Set empty array so page still loads
          } else {
            // For other errors, still set empty array so page loads
            setSubgroups([]);
          }
        }

        // Load events from this group
        console.log('🔵 [GroupView] Loading events from group...');
        let groupEvents = [];
        try {
          groupEvents = await getEventsByHost('group', groupId);
        } catch (eventError) {
          console.error('🔴 [GroupView] Error loading group events:', eventError);
          groupEvents = [];
        }
        
        // Load events from subgroups
        const subgroupEvents = [];
        for (const subgroup of subgroupsList) {
          try {
            const events = await getEventsByHost('group', subgroup.id);
            subgroupEvents.push(...events);
          } catch (eventError) {
            console.error(`🔴 [GroupView] Error loading events for subgroup ${subgroup.id}:`, eventError);
            // Continue with other subgroups
          }
        }
        
        // Combine all events
        const combinedEvents = [...groupEvents, ...subgroupEvents];
        const now = new Date();
        const upcoming = combinedEvents
          .filter((e) => new Date(e.startsAt) >= now)
          .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
        
        console.log('🔵 [GroupView] All events loaded:', combinedEvents.length);
        setAllEvents(combinedEvents);

        console.log('🔵 [GroupView] All data loaded successfully, setting loading to false');
        setLoading(false);
      } catch (subError) {
        console.error('🔴 [GroupView] Error loading group details:', subError);
        setLoading(false);
        setError(subError.message || 'Failed to load some group details');
      }
    } catch (err) {
      console.error('🔴 [GroupView] Error loading group:', err);
      setError(err.message || 'Failed to load group');
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user || !group) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const fileRef = ref(storage, `group-images/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      
      await updateGroup(groupId, { image: downloadURL });
      setGroup({ ...group, image: downloadURL });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== 'DELETE' || !group) {
      console.log('🔴 [GroupView] Delete validation failed:', { deleteConfirmText, hasGroup: !!group });
      return;
    }

    console.log('🔵 [GroupView] Starting delete process for group:', groupId);
    setDeleting(true);
    setError('');
    try {
      console.log('🔵 [GroupView] Calling deleteGroup...');
      await deleteGroup(groupId);
      console.log('✅ [GroupView] Group deleted successfully, navigating to home');
      navigate('/home');
    } catch (err) {
      console.error('🔴 [GroupView] Error deleting group:', err);
      console.error('🔴 [GroupView] Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      setError(err.message || 'Failed to delete group');
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  // Get events for current tab
  const getEventsForTab = () => {
    const now = new Date();
    switch (activeTab) {
      case 'hosting':
        return allEvents.filter((e) => e.hostId === user?.uid && new Date(e.startsAt) >= now);
      case 'invites':
        // TODO: Load actual invites for this group context
        return [];
      case 'upcoming':
      default:
        return allEvents.filter((e) => new Date(e.startsAt) >= now);
    }
  };

  // Helper function to get event card color (same as Home page)
  const getEventCardColor = (eventId) => {
    const colors = [
      { accent: '#00d9ff' }, // Teal
      { accent: '#ff6b35' }, // Orange
      { accent: '#00ffb8' }, // Teal-green
      { accent: '#ffaa00' }, // Orange-yellow
      { accent: '#00b8d4' }, // Darker teal
      { accent: '#ff8c42' }, // Lighter orange
    ];
    const hash = eventId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Helper function to get location display (same as Home page)
  const getLocationDisplay = (event) => {
    if (event.location?.city && event.location?.state) {
      return `${event.location.city}, ${event.location.state}`;
    } else if (event.location?.text && event.location.text !== 'TBD') {
      // Extract city/state from full address if possible
      const parts = event.location.text.split(',');
      if (parts.length >= 2) {
        const city = parts[parts.length - 2]?.trim();
        const state = parts[parts.length - 1]?.trim().split(' ')[0];
        if (city && state) {
          return `${city}, ${state}`;
        }
      }
      return event.location.text;
    }
    return 'Location TBD';
  };

  // Owner: from members list OR from group.ownerId (covers auto-join/legacy groups where owner might not be in members)
  const isOwner = userRole === 'owner' || (group?.ownerId === user?.uid);
  const isCohost = userRole === 'cohost';
  const isMember = userRole !== null;
  const canEdit = isOwner || isCohost || userIsAdmin;

  // Notify TopNavBar of permissions so gear menu and Edit show (must be before early returns)
  useEffect(() => {
    if (groupId && (userRole !== null || group?.ownerId === user?.uid || userIsAdmin)) {
      window.dispatchEvent(new CustomEvent('groupPermissionsUpdate', {
        detail: { groupId, canEdit, isOwner, isCohost }
      }));
    }
  }, [groupId, canEdit, isOwner, isCohost, userRole, group?.ownerId, user?.uid, userIsAdmin]);

  if (loading) {
    return (
      <div className="group-view-container">
        <div className="loading">Loading group...</div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="group-view-container">
        <div className="error-message">{error || 'Group not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  // Get first letter for default avatar
  const firstLetter = group.name ? group.name.charAt(0).toUpperCase() : 'G';

  const handleJoinGroup = async () => {
    if (!user || !groupId || joining) return;

    setJoining(true);
    try {
      // If there's a notification, resolve the invite token
      let role = 'member';
      let sponsoredBy = null;

      if (notificationId) {
        try {
          const notificationDoc = await getDoc(doc(db, 'notifications', notificationId));
          if (notificationDoc.exists()) {
            const notifData = notificationDoc.data();
            if (notifData.inviteToken) {
              const invite = await resolveInviteToken(notifData.inviteToken);
              if (invite) {
                role = invite.metadata?.role || 'member';
                sponsoredBy = invite.metadata?.sponsorId || invite.issuedBy;
              }
            }
            // Mark notification as read
            await updateDoc(doc(db, 'notifications', notificationId), {
              status: 'read',
              readAt: new Date(),
            });
          }
        } catch (notifError) {
          console.error('Error processing notification:', notifError);
        }
      }

      // Add user to group
      const memberData = {
        userId: user.uid,
        role,
        joinedAt: new Date(),
      };
      
      // Only add sponsoredBy if it's a valid string
      if (sponsoredBy && typeof sponsoredBy === 'string') {
        memberData.sponsoredBy = sponsoredBy;
      }
      
      await addGroupMember(groupId, memberData);

      // Mark all pending notifications for this group as read
      await markAllGroupNotificationsAsRead(groupId);

      // Reload group data to update membership
      await loadGroupData();
      setShowJoinModal(false);
      
      // Remove notificationId from URL
      navigate(`/groups/${groupId}?joined=true`, { replace: true });
    } catch (error) {
      console.error('Error joining group:', error);
      alert('Failed to join group. Please try again.');
      setJoining(false);
    }
  };

  const handleDeclineJoin = async () => {
    if (notificationId) {
      try {
        await updateDoc(doc(db, 'notifications', notificationId), {
          status: 'read',
          readAt: new Date(),
        });
      } catch (notifError) {
        console.error('Error updating notification:', notifError);
      }
    }
    setShowJoinModal(false);
    navigate('/home');
  };

  const markAllGroupNotificationsAsRead = async (targetGroupId) => {
    if (!user || !targetGroupId) return;
    
    try {
      console.log('🔵 [GroupView] Marking all notifications for group as read:', targetGroupId);
      
      // Find all unread notifications for this group
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', user.uid),
        where('groupId', '==', targetGroupId),
        where('status', '==', 'unread')
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log('🔵 [GroupView] No pending notifications to mark as read');
        return;
      }
      
      // Use batch write for efficiency
      const batch = writeBatch(db);
      snapshot.docs.forEach((docSnapshot) => {
        const notificationRef = doc(db, 'notifications', docSnapshot.id);
        batch.update(notificationRef, {
          status: 'read',
          readAt: new Date(),
        });
      });
      
      await batch.commit();
      console.log(`🔵 [GroupView] Marked ${snapshot.docs.length} notifications as read`);
    } catch (error) {
      console.error('🔴 [GroupView] Error marking group notifications as read:', error);
    }
  };

  return (
    <div className="group-view-container">
      <div className="group-view-header">
        <div className="group-header-content">
          <div className="group-image-section">
            <label 
              className="group-image-wrapper" 
              style={{ cursor: canEdit ? 'pointer' : 'default' }}
              onClick={canEdit ? () => fileInputRef.current?.click() : undefined}
            >
              {group.image ? (
                <img src={group.image} alt={group.name} className="group-image" />
              ) : (
                <div className="group-image-default">
                  <span>{group.name ? group.name.charAt(0).toUpperCase() : '📁'}</span>
                </div>
              )}
              {canEdit && (
                <>
                  <div className="group-image-overlay">
                    <span className="group-image-edit-icon">📷</span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingImage}
                  />
                </>
              )}
            </label>
            {uploadingImage && <div className="uploading-indicator">Uploading...</div>}
          </div>

          <div className="group-info-section">
            <div className="group-title-row" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <h1 className="group-title" style={{ margin: 0, flex: 1 }}>{group.name}</h1>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => navigate(`/groups/${groupId}/settings`)}
                  className="btn-edit-event"
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--background-alt)';
                    e.currentTarget.style.borderColor = 'var(--primary-color)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = 'var(--border-light)';
                  }}
                  title="Edit Group"
                >
                  <span>✏️</span>
                  <span>Edit</span>
                </button>
              )}
              {/* Invite Button - To the right of group name */}
              {canInvite && (
                <button
                  className="btn-invite-header"
                  onClick={() => setShowQRModal(true)}
                  title="Generate QR code to invite/sponsor others"
                >
                  Invite
                </button>
              )}
            </div>
            {group.description && (
              <div className="group-description-wrapper" ref={descriptionContainerRef}>
                <p 
                  ref={descriptionRef}
                  className={`group-description ${descriptionExpanded ? 'expanded' : ''}`}
                >
                  {group.description}
                </p>
                {(showDescriptionMore || group.description.length > 120) && (
                  <button
                    className="btn-description-toggle"
                    onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  >
                    {descriptionExpanded ? '...less' : '...more'}
                  </button>
                )}
              </div>
            )}
            {group.website && (
              <div className="event-website-section" style={{ marginTop: '0.5rem' }}>
                <a
                  href={group.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-website-link"
                >
                  <span className="website-icon">🔗</span>
                  <span>Group website</span>
                </a>
              </div>
            )}
            <div className="group-badges">
              <span className="badge badge-visibility">{group.visibility}</span>
              {isOwner && <span className="badge badge-owner">Owner</span>}
              {isCohost && <span className="badge badge-cohost">Cohost</span>}
              {(() => {
                console.log('🔵 [GroupView] Rendering badges section:', { isOwner, isCohost, groupId, userRole, canEdit });
                return null;
              })()}
              {isOwner && (
                <button
                  type="button"
                  className="badge badge-edit"
                  style={{ position: 'relative', zIndex: 1000 }}
                  onMouseDown={(e) => {
                    console.log('🔵 [GroupView] Cohosts button mousedown');
                    e.stopPropagation();
                  }}
                  onMouseUp={(e) => {
                    console.log('🔵 [GroupView] Cohosts button mouseup');
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    console.log('🔵 [GroupView] Cohosts button onClick FIRED');
                    e.preventDefault();
                    e.stopPropagation();
                    const targetPath = `/groups/${groupId}/cohosts`;
                    console.log('🔵 [GroupView] Cohosts button clicked, navigating to:', targetPath);
                    console.log('🔵 [GroupView] Current pathname:', window.location.pathname);
                    console.log('🔵 [GroupView] groupId:', groupId);
                    
                    // Use window.location for more reliable navigation
                    try {
                      console.log('🔵 [GroupView] About to navigate to:', targetPath);
                      // Try React Router navigate first
                      navigate(targetPath, { replace: false });
                      console.log('🟢 [GroupView] Navigation called successfully');
                      
                      // Fallback: if navigation doesn't work after a short delay, use window.location
                      setTimeout(() => {
                        if (window.location.pathname !== targetPath) {
                          console.warn('🟡 [GroupView] React Router navigate may have failed, using window.location');
                          window.location.href = targetPath;
                        } else {
                          console.log('🟢 [GroupView] Navigation successful, path is:', window.location.pathname);
                        }
                      }, 50);
                    } catch (err) {
                      console.error('🔴 [GroupView] Navigation error:', err);
                      // Fallback to window.location
                      window.location.href = targetPath;
                    }
                  }}
                  title="Manage Cohosts"
                >
                  Cohosts
                </button>
              )}
              {!isOwner && console.log('🔴 [GroupView] Cohosts button NOT rendered - isOwner is false:', { isOwner, userRole, groupId })}
              {canEdit && (
                <button
                  className="badge badge-edit"
                  onClick={() => navigate(`/groups/${groupId}/settings`)}
                  title="Edit Group"
                >
                  Edit
                </button>
              )}
              {isMember && !isOwner && (
                <button
                  className="badge badge-leave"
                  onClick={async () => {
                    if (confirm('Are you sure you want to leave this group? You\'ll lose access to all group events and message boards.')) {
                      try {
                        await removeGroupMember(groupId, user.uid);
                        navigate('/home');
                      } catch (error) {
                        console.error('Error leaving group:', error);
                        alert('Failed to leave group. Please try again.');
                      }
                    }
                  }}
                  title="Leave Group"
                >
                  Leave
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top Bar with Create Button */}
      <div className="group-top-bar" style={{ position: 'relative' }}>
        <div className="floating-create-wrapper" ref={createDropdownRef}>
          <button
            className="floating-create-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowCreateDropdown(!showCreateDropdown);
            }}
            title="Create"
            disabled={!canCreate}
          >
            +
          </button>
          {showCreateDropdown && canCreate && (
            <>
              {/* Backdrop to close dropdown */}
              <div 
                className="dropdown-backdrop"
                onClick={() => setShowCreateDropdown(false)}
              />
              <div className="create-dropdown floating">
                <div
                  className="create-dropdown-item"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCreateDropdown(false);
                    navigate(`/create-group?parentType=group&parentId=${groupId}`);
                  }}
                >
                  <div className="create-dropdown-title">📁 Create Subgroup</div>
                  <div className="create-dropdown-description">
                    Create a subgroup within this group
                  </div>
                </div>
                <div
                  className="create-dropdown-item"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCreateDropdown(false);
                    navigate(`/create-event?hostType=group&hostId=${groupId}`);
                  }}
                >
                  <div className="create-dropdown-title">🎉 Create Event</div>
                  <div className="create-dropdown-description">
                    Create an event under this group
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <main className="group-view-main" style={{ position: 'relative' }}>
        {/* Blur overlay for non-members - covers all content except header */}
        {!isMember && (
          <div className="group-blur-overlay">
            <div className="group-blur-message">
              <p>To see the details of this group you must join</p>
              <div className="group-join-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleJoinGroup}
                  disabled={joining}
                >
                  {joining ? 'Joining...' : 'Join Group'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleDeclineJoin}
                  disabled={joining}
                >
                  Decline
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Members Section - Horizontal scrolling (show for all members so Chat/Broadcast cards are visible) */}
        {isMember && (
          <section className="members-section">
            <div className="members-header">
              <h2>Members ({membersWithData.length})</h2>
            </div>
            <div className="members-scroll-container">
              <div className="members-row">
                {/* Chat Button - Card style (same placement as iPhone app); show for all members */}
                <div
                  className="member-card-square member-card-chat"
                  onClick={() => {
                    if (isGroupChatAvailable(group)) {
                      window.dispatchEvent(new CustomEvent('openChat', {
                        detail: { type: 'group', id: groupId, returnTo: `/groups/${groupId}` }
                      }));
                    } else {
                      // Chat not enabled - still open drawer; owner can enable in Settings
                      window.dispatchEvent(new CustomEvent('openChat', {
                        detail: { type: 'group', id: groupId, returnTo: `/groups/${groupId}` }
                      }));
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  title={isGroupChatAvailable(group) ? 'Open Group Chat' : 'Group Chat'}
                >
                  <div className="member-card-image member-card-chat-image">
                    <div className="member-card-chat-icon">💬</div>
                  </div>
                  <div className="member-card-name member-card-chat-text">Chat</div>
                </div>

                {/* Broadcast Button - Only for owners/cohosts */}
                {canEdit && (
                  <div
                    className="member-card-square member-card-broadcast"
                    onClick={() => setShowBroadcastModal(true)}
                    style={{ cursor: 'pointer' }}
                    title="Broadcast message to all members"
                  >
                    <div className="member-card-image member-card-broadcast-image">
                      <div className="member-card-broadcast-icon">📢</div>
                    </div>
                    <div className="member-card-name member-card-broadcast-text">Broadcast</div>
                  </div>
                )}
                
                {/* Sponsor/Invite Button - Empty member card style */}
                {canInvite && (
                  <>
                    <div
                      className="member-card-square member-card-invite"
                      onClick={() => setShowSponsorModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="member-card-image member-card-invite-image">
                        <div className="member-card-invite-icon">+</div>
                      </div>
                      <div className="member-card-name member-card-invite-text">Sponsor/Invite</div>
                    </div>
                    {/* Invite Friend Button */}
                    <div
                      className="member-card-square member-card-invite-friend"
                      onClick={() => setShowInviteFriendModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="member-card-image member-card-invite-image">
                        <div className="member-card-invite-icon">👤</div>
                      </div>
                      <div className="member-card-name member-card-invite-text">Invite Friend</div>
                    </div>
                  </>
                )}
                
                {membersWithData.map((member) => {
                  const displayName = member.firstName && member.lastName
                    ? `${member.firstName} ${member.lastName}`.trim()
                    : member.firstName || member.lastName || 'Member';
                  const initials = displayName
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);
                  
                  // Get role label
                  const roleLabel = member.role === 'owner' ? 'Leader' : 
                                   member.role === 'cohost' ? 'Cohost' : 
                                   'Member';
                  
                  return (
                    <div
                      key={member.userId}
                      className="member-card-square"
                      onClick={() => {
                        if (member.userId !== user?.uid) {
                          setSelectedUserId(member.userId);
                          setShowProfileModal(true);
                        }
                      }}
                      style={{ cursor: member.userId !== user?.uid ? 'pointer' : 'default' }}
                    >
                      <div className="member-card-image">
                        {member.profilePicture ? (
                          <img 
                            src={member.profilePicture} 
                            alt={displayName}
                            onError={(e) => {
                              // Just remove the profile picture if it fails - don't retry to avoid loops
                              console.error(`🔴 [GroupView] Profile picture failed to load for ${member.userId}, removing`);
                              setMembersWithData(prev => prev.map(m => 
                                m.userId === member.userId 
                                  ? { ...m, profilePicture: null }
                                  : m
                              ));
                            }}
                          />
                        ) : (
                          <div className="member-card-avatar">
                            {initials || '👤'}
                          </div>
                        )}
                      </div>
                      <div className="member-card-name">{displayName}</div>
                      <div className="member-card-role">{roleLabel}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Subgroups Section - Horizontal scrolling row (like Home page) */}
        {isMember && subgroups.length > 0 && (
          <section className="groups-section">
            <div className="groups-header">
              <h2>Subgroups</h2>
            </div>
            <div className="groups-scroll-container">
              <div className="groups-row">
                {subgroups
                  .sort((a, b) => {
                    try {
                      const aVisited = localStorage.getItem(`group_visited_${a.id}`);
                      const bVisited = localStorage.getItem(`group_visited_${b.id}`);
                      
                      let aTime = 0;
                      if (aVisited) {
                        aTime = new Date(aVisited).getTime();
                      } else if (a.updatedAt) {
                        aTime = a.updatedAt.toDate ? a.updatedAt.toDate().getTime() : new Date(a.updatedAt).getTime();
                      } else if (a.createdAt) {
                        aTime = a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime();
                      }
                      
                      let bTime = 0;
                      if (bVisited) {
                        bTime = new Date(bVisited).getTime();
                      } else if (b.updatedAt) {
                        bTime = b.updatedAt.toDate ? b.updatedAt.toDate().getTime() : new Date(b.updatedAt).getTime();
                      } else if (b.createdAt) {
                        bTime = b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime();
                      }
                      
                      return bTime - aTime;
                    } catch (error) {
                      console.error('Error sorting subgroups:', error);
                      return 0;
                    }
                  })
                  .map((subgroup) => (
                    <div
                      key={subgroup.id}
                      className="group-card-square"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        localStorage.setItem(`group_visited_${subgroup.id}`, new Date().toISOString());
                        navigate(`/groups/${subgroup.id}`);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="group-card-image">
                        {subgroup.image ? (
                          <img src={subgroup.image} alt={subgroup.name} />
                        ) : (
                          <div className="group-card-avatar">
                            {subgroup.name ? subgroup.name.charAt(0).toUpperCase() : '📁'}
                          </div>
                        )}
                      </div>
                      <div className="group-card-name">{subgroup.name || 'Unnamed Group'}</div>
                    </div>
                  ))}
              </div>
            </div>
          </section>
        )}

        {/* Tabs Navigation - Only show for members */}
        {isMember && (
          <>
            <div className="tabs-container">
              <button
                className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
                onClick={() => setActiveTab('upcoming')}
              >
                Upcoming
              </button>
              <button
                className={`tab ${activeTab === 'invites' ? 'active' : ''}`}
                onClick={() => setActiveTab('invites')}
              >
                Invites
              </button>
              <button
                className={`tab ${activeTab === 'hosting' ? 'active' : ''}`}
                onClick={() => setActiveTab('hosting')}
              >
                Hosting
              </button>
            </div>

            {/* Events Content */}
            <section className="home-section">
          {getEventsForTab().length === 0 ? (
            <div className="empty-state">
              <p>
                {activeTab === 'upcoming' && 'No upcoming events in this group.'}
                {activeTab === 'invites' && 'No invites yet.'}
                {activeTab === 'hosting' && "You're not hosting any events in this group yet."}
              </p>
              {canCreate && activeTab !== 'invites' && (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/create-event?hostType=group&hostId=${groupId}`)}
                >
                  Create Event
                </button>
              )}
            </div>
          ) : (
            <div className="events-grid">
              {getEventsForTab().map((event) => {
                const accentColor = getEventCardColor(event.id).accent;
                const locationDisplay = getLocationDisplay(event);
                
                // Get host name
                const hostName = event.hostType === 'group' && event.hostId === groupId
                  ? group?.name || 'This Group'
                  : event.hostType === 'group'
                  ? 'Another Group'
                  : 'User';
                
                return (
                  <div
                    key={event.id}
                    className="home-event-card"
                    onClick={() => navigate(`/events/${event.id}`)}
                    style={{
                      '--event-accent-color': accentColor,
                    }}
                  >
                    <div className="home-event-image-wrapper">
                      {event.image ? (
                        <img src={event.image} alt={event.title} className="home-event-image" />
                      ) : (
                        <div className="home-event-avatar">
                          {event.title ? (
                            <span>{event.title.charAt(0).toUpperCase()}</span>
                          ) : (
                            <span>🎉</span>
                          )}
                        </div>
                      )}
                      
                      {/* Title bubble overlay at top-left */}
                      <div className="home-event-title-bubble">
                        <span className="home-event-title-text">{event.title}</span>
                      </div>
                      
                      {/* Hosted by and location overlay at bottom */}
                      <div className="home-event-bottom-overlay">
                        {event.description && (
                          <div className="home-event-description-overlay">
                            {event.description}
                          </div>
                        )}
                        <div className="home-event-hosted-by">
                          Hosted by {hostName}
                        </div>
                        <div className="home-event-location-overlay">
                          {locationDisplay}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
            </section>

            {/* Sponsorship Rules and Sponsored Members Section */}
            {canInvite && group.rules?.membersCanInvite && (
              <section className="home-section">
                {/* Only show header if there are rules to display */}
                {(group.rules.maxSponsorsPerMember !== undefined || 
                  group.rules.sponsorEligibility?.requireOneEvent || 
                  group.rules.sponsorAccountabilityEnabled) && (
                  <div className="section-header">
                    <h2 className="section-title">Sponsorship Rules</h2>
                  </div>
                )}
                
                {/* Sponsorship Rules Display */}
                {(group.rules.maxSponsorsPerMember !== undefined || 
                  group.rules.sponsorEligibility?.requireOneEvent || 
                  group.rules.sponsorAccountabilityEnabled) && (
                  <div className="sponsorship-rules-display">
                    {group.rules.maxSponsorsPerMember !== undefined && (
                      <p className="sponsor-rule-item">
                        <strong>Max Sponsorships:</strong> {
                          group.rules.maxSponsorsPerMember === 0 
                            ? 'Members cannot sponsor other members.'
                            : `You can sponsor up to ${group.rules.maxSponsorsPerMember} ${group.rules.maxSponsorsPerMember === 1 ? 'person' : 'people'}.`
                        }
                      </p>
                    )}
                    {group.rules.sponsorEligibility?.requireOneEvent && (
                      <p className="sponsor-rule-item">
                        <strong>Eligibility:</strong> You must attend one event before sponsoring others.
                      </p>
                    )}
                    {group.rules.sponsorAccountabilityEnabled && (
                      <div className="sponsor-rule-item">
                        <strong>Accountability:</strong> {group.rules.sponsorAccountabilityText ? (
                          <span>{group.rules.sponsorAccountabilityText}</span>
                        ) : (
                          <span>You are responsible for the members you sponsor. If they violate group rules, you may both be suspended.</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Sponsored Members Management - Only show if members can invite */}
                <div className="sponsored-members-section">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowSponsoredMembers(!showSponsoredMembers)}
                  >
                    {showSponsoredMembers ? 'Hide' : 'View'} Sponsored Members ({sponsoredMembers.length})
                  </button>
                  
                  {showSponsoredMembers && (
                    <div className="sponsored-members-list">
                      <div className="sponsored-members-grid">
                        {sponsoredMembersWithData.map((member) => {
                          const displayName = member.firstName && member.lastName
                            ? `${member.firstName} ${member.lastName}`.trim()
                            : member.firstName || member.lastName || 'Member';
                          const initials = displayName
                            .split(' ')
                            .map(n => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2);
                          
                          return (
                            <div key={member.userId} className="sponsored-member-card">
                              <div className="sponsored-member-image">
                                {member.profilePicture ? (
                                  <img src={member.profilePicture} alt={displayName} />
                                ) : (
                                  <div className="sponsored-member-avatar">
                                    {initials || '👤'}
                                  </div>
                                )}
                              </div>
                              <div className="sponsored-member-name">{displayName}</div>
                              <div className="sponsored-member-status">
                                {member.status === 'accepted' || (member.userId && members.find(m => m.userId === member.userId))
                                  ? '✅ Accepted' 
                                  : '⏳ Pending'}
                              </div>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={async () => {
                                  const action = member.status === 'pending' 
                                    ? `Cancel pending invite for ${displayName}?`
                                    : `Remove ${displayName} from the group? This will revoke their membership.`;
                                  
                                  if (confirm(action)) {
                                    try {
                                      if (member.status === 'pending' && member.inviteId) {
                                        // Delete pending invite token
                                        await deleteDoc(doc(db, 'invites', member.inviteId));
                                        // Also try to delete related notifications
                                        try {
                                          const notificationsRef = collection(db, 'notifications');
                                          const notifQuery = query(
                                            notificationsRef,
                                            where('groupId', '==', groupId),
                                            where('type', '==', 'group_sponsor'),
                                            where('inviteToken', '==', member.inviteId)
                                          );
                                          const notifSnapshot = await getDocs(notifQuery);
                                          const batch = writeBatch(db);
                                          notifSnapshot.docs.forEach(docSnapshot => {
                                            batch.delete(docSnapshot.ref);
                                          });
                                          if (notifSnapshot.docs.length > 0) {
                                            await batch.commit();
                                          }
                                        } catch (err) {
                                          console.log('No notification found to delete:', err);
                                        }
                                      } else if (member.userId) {
                                        // Remove accepted member
                                        await removeGroupMember(groupId, member.userId);
                                      }
                                      
                                      // Reload data
                                      await loadGroupData();
                                    } catch (error) {
                                      console.error('Error removing member/invite:', error);
                                      alert('Failed to remove. Please try again.');
                                    }
                                  }
                                }}
                              >
                                {member.status === 'pending' ? 'Cancel' : 'Remove'}
                              </button>
                            </div>
                          );
                        })}
                        
                        {/* Add new sponsorship slot button */}
                        <div 
                          className="sponsored-member-card sponsored-member-add"
                          onClick={() => setShowSponsorModal(true)}
                        >
                          <div className="sponsored-member-add-icon">+</div>
                          <div className="sponsored-member-add-text">Sponsor Someone</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Sponsor Modal */}
      {showSponsorModal && (
        <SponsorModal
          isOpen={showSponsorModal}
          onClose={() => setShowSponsorModal(false)}
          groupId={groupId}
          groupName={group?.name}
          userId={user?.uid}
          userRole={userRole}
          groupRules={group?.rules}
        />
      )}

      {showInviteFriendModal && (
        <InviteFriendModal
          isOpen={showInviteFriendModal}
          onClose={() => setShowInviteFriendModal(false)}
          targetKind="group_join"
          targetId={groupId}
          targetName={group?.name}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => {
          if (!deleting) {
            setShowDeleteConfirm(false);
            setDeleteConfirmText('');
            setError('');
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Delete Group</h2>
            <p className="modal-warning">
              This action cannot be undone. All events, members, and data will be permanently deleted.
            </p>
            {error && (
              <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(255, 68, 68, 0.1)', border: '1px solid var(--error)', borderRadius: 'var(--radius)' }}>
                {error}
              </div>
            )}
            <div className="modal-input-group">
              <label htmlFor="deleteConfirm">
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                id="deleteConfirm"
                type="text"
                className="input"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                disabled={deleting}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                  setError('');
                }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting || deleteConfirmText !== 'DELETE'}
              >
                {deleting ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {user && (
        <QRCodeModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          groupId={groupId}
          issuedBy={user.uid}
        />
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
      />
      
      {/* Broadcast Modal */}
      {user && (
        <BroadcastModal
          isOpen={showBroadcastModal}
          onClose={() => setShowBroadcastModal(false)}
          groupId={groupId}
          groupName={group?.name}
          user={user}
        />
      )}

      {/* Join Modal - Shows when user is not a member and has notificationId */}
      {showJoinModal && !isMember && group && (
        <div className="join-modal-overlay" onClick={handleDeclineJoin}>
          <div className="join-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Join Group</h2>
            <p>You've been invited to join <strong>{group.name}</strong></p>
            {group.description && (
              <p className="join-modal-description">{group.description}</p>
            )}
            <p className="join-modal-message">
              To see the details of this group you must join
            </p>
            <div className="join-modal-actions">
              <button
                className="btn btn-primary"
                onClick={handleJoinGroup}
                disabled={joining}
              >
                {joining ? 'Joining...' : 'Join Group'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleDeclineJoin}
                disabled={joining}
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupView;
